import { isPopularShefSegment } from 'common/MealTypeUtils';
import { urlDateFormat } from 'common/UrlDate';
import { filter } from 'lodash';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { RouteChildrenProps } from 'react-router';
import { OrderShefFragment, ShefMenuSegmentFragment } from 'src/gqlReactTypings.generated.d';
import {
  SegmentHeader,
  SegmentHeaderContainer,
  SegmentHeaderRow,
  SegmentHeaderTitle,
} from 'src/pages/consumer/dish-first/explore/styles';
import {
  ConsumerOrderDishSelectionItem,
  ConsumerOrderDishSelectionItemFormat,
  DishSelectionFoodItem,
} from 'src/pages/consumer/order/components/ConsumerOrderDishSelectionItem';
import { Carousel } from 'src/shared/design-system/DishFirst/Carousel';
import { useSelector } from 'src/store';
import { selectMenuItemCountById } from 'src/store/cart/selectors';
import { getFirstMatchingAvailabilityOrFlexible } from '../components/ConsumerOrderDishSelection/hooks';
import { SegmentRowContainer, ShefMenuSegmentRowContainer } from './styles';

interface IShefMenuSegmentProps {
  segment: ShefMenuSegmentFragment;
  location: RouteChildrenProps['location'];
  match: RouteChildrenProps['match'];
  isUnavailable?: boolean;
  deliveryDateTime?: DateTime;
  increment?: (foodItem: DishSelectionFoodItem) => void;
  decrement?: (foodItem: DishSelectionFoodItem) => void;
  shef?: OrderShefFragment;
  isEditingOrder: boolean;
  isShefAvailable?: boolean;
  nextDeliveryDate?: DateTime | null;
  isShefAvailableInFuture?: boolean;
  showModalPrompt: boolean;
  openModal: () => void;
}
export const ShefMenuSegmentRow = ({
  segment,
  location,
  match,
  isUnavailable,
  deliveryDateTime,
  increment,
  decrement,
  shef,
  isEditingOrder,
  isShefAvailable,
  nextDeliveryDate,
  isShefAvailableInFuture,
  showModalPrompt,
  openModal,
}: IShefMenuSegmentProps) => {
  const isFoodItem = (item: (typeof segment.content.items)[number]): item is DishSelectionFoodItem =>
    item.__typename === 'FoodItem';

  const items: DishSelectionFoodItem[] = useMemo(
    () => filter(segment.content.items, isFoodItem),
    [segment.content.items]
  );

  const remainingQuantities = items.reduce<Record<number, number>>((quantities, foodItem) => {
    const availability = getFirstMatchingAvailabilityOrFlexible(deliveryDateTime, foodItem.availability);
    const numFoodItemRemaining = foodItem.quantityRemaining ?? availability?.numAvailable ?? 0;

    return {
      ...quantities,
      [foodItem.id]: numFoodItemRemaining,
    };
  }, {});

  const { menuCounts } = useSelector((state) => {
    const deliveryUrlDate = deliveryDateTime ? deliveryDateTime.toFormat(urlDateFormat) : undefined;
    return {
      menuCounts: selectMenuItemCountById(
        isEditingOrder ? state.editableOrderCart : state.cart,
        shef?.id ?? '',
        deliveryUrlDate
      ),
    };
  });
  if (items.length === 0) return null;

  return (
    <ShefMenuSegmentRowContainer>
      <SegmentHeaderContainer>
        <SegmentHeaderRow>
          <SegmentHeader>
            <SegmentHeaderTitle>{segment.displayName}</SegmentHeaderTitle>
          </SegmentHeader>
        </SegmentHeaderRow>
      </SegmentHeaderContainer>

      <SegmentRowContainer>
        <Carousel
          content={segment.content.items}
          scrollButtonsElementName={segment.id}
          showMobileDivider
          showScrollButtons>
          {items.map((item) => (
            <ConsumerOrderDishSelectionItem
              showDishTags={false}
              format={ConsumerOrderDishSelectionItemFormat.ORDER}
              key={item.id}
              foodItem={{ ...item, isPopular: isPopularShefSegment(segment.displayName) || item.isPopular }}
              location={location}
              isUnavailable={isUnavailable}
              remainingQuantity={remainingQuantities[item.id]}
              quantity={menuCounts[item.id] ?? 0}
              match={match}
              increment={increment}
              decrement={decrement}
              shef={shef}
              isFluid={false}
              showPadding={false}
              shortCard={true}
              showDetails={false}
              isShefAvailable={isShefAvailable && !!deliveryDateTime}
              nextDeliveryDate={nextDeliveryDate}
              isShefAvailableInFuture={isShefAvailableInFuture}
              showModalPrompt={showModalPrompt}
              openModal={openModal}
              autoWidth={true}
              isEditingOrder={isEditingOrder}
            />
          ))}
        </Carousel>
      </SegmentRowContainer>
    </ShefMenuSegmentRowContainer>
  );
};
